// Schema condivisi per il Documento Confezionamento (CON).
// Modello: 1 CON per giornata, N righe (DocumentConLine) una per operatore.
// Al CLOSE: per ogni line decrementa LotPosition.residualQty (sfuso) e crea
// floor((loose + qtyPiecesProduced) / piecesPerBox) FinishedBox.

import { z } from 'zod';

const documentStatusSchema = z.enum(['DRAFT', 'CLOSED', 'SUPERSEDED']);
const boxStatusSchema = z.enum(['PRONTA', 'ALLOCATA', 'SPEDITA', 'SMALTITA', 'RIENTRATA']);

// =========================================================
// Line schemas (input)
// =========================================================

export const conLineDraftSchema = z.object({
  // null = nuova riga; uuid = update esistente
  id: z.string().uuid().nullable().optional(),
  lineNumber: z.coerce.number().int().positive(),
  operatorName: z.string().trim().min(1, 'Operatore richiesto').max(80),
  lotPositionId: z.string().uuid('LotPosition richiesta'),
  qtyConsumedG: z.coerce.number().nonnegative('Grezzo consumato >= 0'),
  productSkuId: z.string().uuid('ProductSku richiesto'),
  qtyPiecesProduced: z.coerce.number().int().nonnegative('Pezzi prodotti >= 0'),
  note: z.string().max(500).nullable().optional(),
});
export type ConLineDraft = z.infer<typeof conLineDraftSchema>;

// =========================================================
// Document request schemas
// =========================================================

// Crea un nuovo CON per una giornata specifica. Workflow tipico:
// pulsante "Nuovo CON per oggi" se non esiste gia' per la data.
export const conCreateRequestSchema = z.object({
  workDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data formato YYYY-MM-DD'),
  notes: z.string().max(2000).nullable().optional(),
});
export type ConCreateRequest = z.infer<typeof conCreateRequestSchema>;

// Update DRAFT: riscrive l'array di righe (sostituzione completa di lines
// referenziate da id, le altre vengono ricreate / le mancanti rimosse).
export const conUpdateRequestSchema = z.object({
  notes: z.string().max(2000).nullable().optional(),
  lines: z.array(conLineDraftSchema).max(200),
});
export type ConUpdateRequest = z.infer<typeof conUpdateRequestSchema>;

// Close: trigger transizione DRAFT -> CLOSED. Genera FinishedBox + StockMovement.
export const conCloseRequestSchema = z.object({}).strict();
export type ConCloseRequest = z.infer<typeof conCloseRequestSchema>;

// =========================================================
// Response schemas
// =========================================================

export const conLineResponseSchema = z.object({
  id: z.string().uuid(),
  lineNumber: z.number().int().positive(),
  operatorName: z.string(),
  lotPositionId: z.string().uuid(),
  lotCode: z.string().nullable(),
  lotProductCode: z.string().nullable(),
  lotProductName: z.string().nullable(),
  qtyConsumedG: z.string(), // Decimal serializzato come string
  productSkuId: z.string().uuid(),
  productSkuCode: z.string().nullable(),
  productSkuGrammatura: z.number().int().nullable(),
  productSkuPiecesPerBox: z.number().int().nullable(),
  qtyPiecesProduced: z.number().int(),
  // Derivati (popolati solo dopo CLOSE)
  qtyTheoreticalG: z.string(),
  sfridoG: z.string(),
  sfridoPct: z.string(),
  boxesCreated: z.number().int(),
  note: z.string().nullable(),
});
export type ConLineResponse = z.infer<typeof conLineResponseSchema>;

export const conResponseSchema = z.object({
  id: z.string().uuid(),
  type: z.literal('CON'),
  code: z.string().nullable(),
  workDate: z.string(),
  periodId: z.string(),
  period: z.object({
    year: z.number().int(),
    month: z.number().int(),
    status: z.string(),
  }),
  status: documentStatusSchema,
  locked: z.boolean(),
  notes: z.string().nullable(),
  createdById: z.string().uuid(),
  closedById: z.string().uuid().nullable(),
  createdAt: z.string(),
  closedAt: z.string().nullable(),
  updatedAt: z.string(),
  lines: z.array(conLineResponseSchema),
  // Aggregati derivati
  totQtyConsumedG: z.string(),
  totPiecesProduced: z.number().int(),
  totBoxesCreated: z.number().int(),
  totSfridoG: z.string(),
});
export type ConResponse = z.infer<typeof conResponseSchema>;

export const conListItemSchema = z.object({
  id: z.string().uuid(),
  code: z.string().nullable(),
  workDate: z.string(),
  status: documentStatusSchema,
  locked: z.boolean(),
  totQtyConsumedG: z.string(),
  totPiecesProduced: z.number().int(),
  totBoxesCreated: z.number().int(),
  totSfridoG: z.string(),
  righeCount: z.number().int(),
  createdAt: z.string(),
  closedAt: z.string().nullable(),
});
export type ConListItem = z.infer<typeof conListItemSchema>;

export const conListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  status: documentStatusSchema.optional(),
  periodId: z.string().uuid().optional(),
  format: z.enum(['json', 'xlsx']).default('json'),
});
export type ConListQuery = z.infer<typeof conListQuerySchema>;

export const conListResponseSchema = z.object({
  items: z.array(conListItemSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});
export type ConListResponse = z.infer<typeof conListResponseSchema>;

// =========================================================
// FinishedBox response (usato da CON, MSS/MSF, INV, /finished-boxes)
// =========================================================

export const finishedBoxResponseSchema = z.object({
  id: z.string().uuid(),
  qrCode: z.string(),
  productSkuId: z.string().uuid(),
  productSkuCode: z.string().nullable(),
  productSkuGrammatura: z.number().int().nullable(),
  productName: z.string().nullable(),
  productDriveLegendId: z.number().int().nullable(),
  lotPositionId: z.string().uuid(),
  lotCode: z.string().nullable(),
  qtyPieces: z.number().int(),
  status: boxStatusSchema,
  packagedByConId: z.string().uuid(),
  packagedByConCode: z.string().nullable(),
  packagedAt: z.string(),
  allocatedToDocId: z.string().uuid().nullable(),
  shippedByDocId: z.string().uuid().nullable(),
  shippedAt: z.string().nullable(),
});
export type FinishedBoxResponse = z.infer<typeof finishedBoxResponseSchema>;

export { boxStatusSchema };
